import { useCallback } from 'react';
import { useTranslation, LOCALE_IDS } from '@parsina/framework/clinical/src/i18n/index';
import { lookupLocal, fillTemplate } from './brandText';

// ─────────────────────────────────────────────────────────────────────────────
// Strings for the mobile پذیرش redesign (docs/DOCTOR_MOBILE_RECEPTION_REDESIGN.md)
// that the kit's `dashboard` catalogue does not carry yet.
//
// They belong in `packages/clinical/src/i18n/translations/{fa,en,ar,fr,de}/
// dashboard.json` under the same keys (the five JSON files beside this module
// are shaped as that file's fragment), and will move there with the next kit
// release. Until then they live here, and `useReceptionStrings` asks the kit
// FIRST: the moment a released catalogue has a key, the kit's wording wins and
// this entry is dead — so the move is a deletion, never a behaviour change.
// ─────────────────────────────────────────────────────────────────────────────

// Nested exactly like the kit's `dashboard.json`, so moving them is a copy.
import fa from './receptionStrings/fa.json';
import en from './receptionStrings/en.json';
import ar from './receptionStrings/ar.json';
import fr from './receptionStrings/fr.json';
import de from './receptionStrings/de.json';

const flatten = (obj, prefix = '', out = {}) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object') flatten(v, key, out); else out[key] = v;
  }
  return out;
};

const STRINGS = { fa: flatten(fa), en: flatten(en), ar: flatten(ar), fr: flatten(fr), de: flatten(de) };

/**
 * Look a key up in the local table — exported for tests. The brand's overlay
 * and tokens apply as in the kit's catalogues (./brandText.js).
 */
export function localString(locale, key, vars) {
  const { value, from } = lookupLocal(STRINGS, locale, key);
  return value == null ? key : fillTemplate(value, vars, from);
}

/**
 * `t` for this screen: the kit catalogue when it has the key, the local table
 * otherwise. Numbers are formatted by the caller (`formatNumber`/`formatDigits`)
 * before they are passed in, so both paths render the same digits.
 */
export function useReceptionStrings() {
  const { t, has, locale } = useTranslation();
  return useCallback(
    (key, vars) => (has(key) ? t(key, vars) : localString(locale, key, vars)),
    [t, has, locale],
  );
}

// Every language the kit ships, not the tables that happen to exist here — so a
// language added to the kit without its table here is a test failure, not a
// screen that quietly falls back. A copy: a test may sort it.
export const RECEPTION_STRING_LOCALES = [...LOCALE_IDS];
export const RECEPTION_STRING_KEYS = Object.keys(STRINGS.fa);
